import { idGenerator } from "Util/uniqueIdGenerator";
import { toRaw } from "vue";

export function createCOLS(COLSFrameSrc) {
    if (!COLSFrameSrc) return {};
    const $idGenerator = new idGenerator();

    let targetOrigin;
    if (COLSFrameSrc) {
        targetOrigin = new URL(COLSFrameSrc).origin;
    }

    const COLS = {
        //Cross-Origin Local Storage (note: this is not a standard acronym)
        frame: null,
        frameLoadPromiseWrapper: {
            promise: null,
            hasLoaded: false,
            reject: null,
            resolve: null
        },
        targetOrigin,
        messagePromises: {}
    };

    /**
     * Use an iframe hostned from a know subdomain that we own (generic.*) to keep local storage.
     * This allows us to share that storage across submdomains.
     * See Web/COLS/cols.html for more information
     */
    COLS.frame = document.createElement("iframe");
    COLS.frame.src = COLSFrameSrc;
    COLS.frame.style.border = "none";
    COLS.frame.style.width = "0";
    COLS.frame.style.height = "0";
    COLS.frame.id = "COLS_frame";
    COLS.frame.title = "COLS_frame";
    COLS.frameLoadPromiseWrapper.promise = new Promise((resolve, reject) => {
        COLS.frameLoadPromiseWrapper.resolve = resolve;
        COLS.frameLoadPromiseWrapper.reject = reject;
    });
    COLS.frame.onload = () => {
        COLS.frameLoadPromiseWrapper.resolve();
        COLS.frameLoadPromiseWrapper.hasLoaded = true;
    };
    COLS.frame.onerror = () => {
        COLS.frameLoadPromiseWrapper.reject("Error loading COLS frame");
    };

    function handleFrameMessage(event) {
        //check for event.source
        if (event.source !== COLS.frame.contentWindow) {
            return;
        }

        const data = event.data;
        // {messageId:<string>, key:<string>, value:<any>}
        if (
            typeof data !== "object" ||
            typeof data.key !== "string" ||
            data.key === "" ||
            data.value === undefined ||
            typeof data.messageId !== "string" ||
            data.messageId === ""
        ) {
            return;
        }

        if (COLS.messagePromises.hasOwnProperty(data.messageId)) {
            const promiseWrapper = COLS.messagePromises[data.messageId];
            delete COLS.messagePromises[data.messageId];
            promiseWrapper.resolve(data.value);
        }
    }
    window.addEventListener("message", handleFrameMessage);

    async function registerAndSendCOLSMessage(action, key, value) {
        const messageId = $idGenerator.getNextId("colsMsg");

        let resolve;
        let reject;
        const promise = new Promise((_resolve, _reject) => {
            resolve = _resolve;
            reject = _reject;
        });
        if (!COLS.frameLoadPromiseWrapper.hasLoaded) {
            await COLS.frameLoadPromiseWrapper.promise;
        }

        COLS.messagePromises[messageId] = {
            promise,
            resolve,
            reject
        };
        const message = {
            messageId,
            action,
            key
        };
        if (action === "set") {
            // need to use toRaw here becaue Vue3 creates proxies in order to track changes.
            // Proxies are not able to be serialized and causes contentWindow.postMessage to fail
            message.value = toRaw(value);
        }
        COLS.frame.contentWindow.postMessage(message, COLS.targetOrigin);
        return promise;
    }

    function setCOLS_item(key, value) {
        const promise = registerAndSendCOLSMessage("set", key, value);
        return promise;
    }
    function getCOLS_item(key) {
        const promise = registerAndSendCOLSMessage("get", key);
        return promise;
    }

    return {
        setCOLS_item,
        getCOLS_item,
        COLSframe: COLS.frame
    };
}
